import React, { ReactNode, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from './Header';
import { DrawerMenu } from './DrawerMenu';
import { BottomNav } from './BottomNav';
import { InstructorBottomNav } from './InstructorBottomNav';

import { StudentStatsProvider } from '@/contexts/StudentStatsContext';
import { useAuth } from '@/components/auth/AuthProvider';
import { useProfile } from '@/hooks/useProfile';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

interface MasterLayoutProps {
  children: ReactNode;
}

const MasterLayoutComponent: React.FC<MasterLayoutProps> = ({ children }) => {
  const { user, isDemo, loading } = useAuth();
  const { profile } = useProfile();
  const location = useLocation();
  
  // Student routes: Show BottomNav on all student pages except auth/landing
  const isStudentRoute = profile?.role === 'student' && !(
    location.pathname === '/' ||
    location.pathname === '/landing' ||
    location.pathname === '/terms' ||
    location.pathname === '/download' ||
    location.pathname === '/qr-register' ||
    location.pathname === '/set-password' ||
     location.pathname === '/reset-password' ||
    location.pathname.startsWith('/auth') ||
    location.pathname.startsWith('/magic-auth') ||
    location.pathname.startsWith('/verify')
  );

  // Instructor routes: Show InstructorBottomNav on instructor pages
  const isInstructorRoute = ['instructor', 'admin'].includes(String(profile?.role)) && [
    '/instructor',
    '/admin'
  ].some(route => location.pathname.startsWith(route));

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <div className="min-h-screen w-full bg-background">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Loading...</h2>
            <p className="text-muted-foreground">
              Please wait while we prepare your experience.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Don't show header/drawer on auth page
  if (!user) {
    return <>{children}</>;
  }

  return (
    <StudentStatsProvider enabled={profile?.role === 'student'}>
      <ErrorBoundary resetKeys={[user?.id]}>
        <div className={`min-h-screen min-h-[100dvh] w-full max-w-[100vw] overflow-x-hidden flex flex-col ${isInstructorRoute ? 'instructor-theme' : ''}`}>
          {isDemo && (
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-center py-2 px-4 text-xs sm:text-sm font-medium" style={{paddingTop: 'max(8px, env(safe-area-inset-top, 0px))'}}>
              🚗 DEMO MODE - This is a preview version for ChatGPT
            </div>
          )}
          <Header />
          {/* Points / Level / Readiness now live inside the student dashboard hero */}
          <DrawerMenu />
          <main className="relative z-1 flex-1 pb-20 w-full max-w-[100vw] overflow-x-hidden overflow-y-auto transition-all duration-300" style={{paddingBottom: isStudentRoute || isInstructorRoute ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : '20px'}}>
            <ErrorBoundary
              resetKeys={[window.location.pathname]}
              fallback={
                <div className="min-h-screen flex items-center justify-center p-4">
                  <div className="text-center p-8 max-w-md">
                    <h2 className="text-xl font-semibold mb-2">Page Error</h2>
                    <p className="text-muted-foreground mb-4">
                      This page encountered an error. Please try navigating to a different page.
                    </p>
                  </div>
                </div>
              }
            >
              {children}
            </ErrorBoundary>
          </main>
          {isStudentRoute && <BottomNav />}
          {isInstructorRoute && <InstructorBottomNav />}
        </div>
      </ErrorBoundary>
    </StudentStatsProvider>
  );
};

// Memoize the layout to prevent unnecessary re-renders
export const MasterLayout = memo(MasterLayoutComponent);